/** @jsx jsx */

/** @jsxFrag */
import { jsx } from '@emotion/react';

import * as React from 'react';

import useRouter from 'js/lib/useRouter';
import user from 'js/lib/user';

import { Button, PageNotification } from '@coursera/cds-core';
import type { BannerName } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import {
  ALTERNATIVE_EMAIL_CACHE_DAYS,
  ALTERNATIVE_EMAIL_COURSE_PROGRESS_CACHE_DAYS,
  STORAGE_KEY,
} from 'bundles/enterprise-legacy-learner-home/constants/forcedSSORecoveryEmail';
import useCheckSignificantCourseProgress from 'bundles/enterprise-legacy-learner-home/queryHooks/useSignificantCourseProgress';
import storageUtils from 'bundles/enterprise-legacy-learner-home/utils/storageUtils';
import { getUnverifiedNonPrimaryEmail } from 'bundles/saml-sso/shared/utils/email';
import { useProgram, useUserEmails } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';

import _t from 'i18n!nls/program-home';

const banners: Record<string, BannerName> = {
  addRecoveryEmail: 'add_recovery_email_banner',
  verifyRecoveryEmail: 'verify_recovery_email_banner',
};

const RecoveryEmailNotification = () => {
  const program = useProgram();
  const { userEmails = [] } = useUserEmails();
  const track = useTracker();
  const router = useRouter();
  const userId = user.get().id;
  const programId = program?.id;
  const unverifiedEmail = getUnverifiedNonPrimaryEmail(userEmails) ?? null;
  const hasSignificantProgress = useCheckSignificantCourseProgress(programId ?? '');
  const thirdPartyOrganization = useThirdPartyOrganization();
  // If user has a verified email, don't show the unverified email banner
  const showUnverifiedEmailNotification =
    userEmails.length > 1 && !userEmails.some((email) => !email.isPrimary && email.isVerified);
  const [isDismissed, setIsDismissed] = React.useState(true);

  const showAddEmail = userEmails?.length === 1 && !showUnverifiedEmailNotification && !isDismissed;

  React.useEffect(() => {
    if (programId) {
      // Check if the user has seen the notification in the last `ALTERNATIVE_EMAIL_CACHE_DAYS` days
      const hasSeenRecoveryEmailNotification = storageUtils.get(userId, programId, STORAGE_KEY, -Infinity) > Date.now();
      setIsDismissed(hasSeenRecoveryEmailNotification);
    }
  }, [programId, userId]);

  React.useEffect(() => {
    if (showAddEmail) {
      track('view_banner', {
        bannerName: banners.addRecoveryEmail,
        ...(thirdPartyOrganization?.slug && { enterprise: { organizationSlug: thirdPartyOrganization.slug } }),
      });
    }
  }, [track, showAddEmail, thirdPartyOrganization?.slug]);

  React.useEffect(() => {
    if (showUnverifiedEmailNotification) {
      track('view_banner', {
        bannerName: banners.verifyRecoveryEmail,
        ...(thirdPartyOrganization?.slug && { enterprise: { organizationSlug: thirdPartyOrganization.slug } }),
      });
    }
  }, [track, showUnverifiedEmailNotification, thirdPartyOrganization?.slug]);

  const openRecoveryModal = () => {
    track('click_banner', {
      bannerName: showAddEmail ? banners.addRecoveryEmail : banners.verifyRecoveryEmail,
      bannerAction: 'open_dialog',
      ...(thirdPartyOrganization?.slug && { enterprise: { organizationSlug: thirdPartyOrganization.slug } }),
    });
    router.push({
      pathname: router.location.pathname,
      params: router.params,
      query: { ...router.location.query, openRecoveryModal: 'true' },
    });
  };

  const onDismiss = () => {
    if (programId) {
      const dismissForDuration = hasSignificantProgress
        ? ALTERNATIVE_EMAIL_COURSE_PROGRESS_CACHE_DAYS
        : ALTERNATIVE_EMAIL_CACHE_DAYS;
      storageUtils.set(userId, programId, STORAGE_KEY, dismissForDuration + Date.now());
      track('click_banner', {
        bannerName: banners.addRecoveryEmail,
        bannerAction: 'dismiss',
        ...(thirdPartyOrganization?.slug && { enterprise: { organizationSlug: thirdPartyOrganization.slug } }),
      });
      setIsDismissed(true);
    }
  };

  return (
    <>
      {showAddEmail && (
        <PageNotification
          title={_t('Protect your accomplishments')}
          severity="information"
          onDismiss={onDismiss}
          className="program-sso-notification"
          actions={
            <Button size="small" variant="primary" onClick={openRecoveryModal}>
              {_t('Add a recovery email')}
            </Button>
          }
        >
          {thirdPartyOrganization?.name
            ? _t(
                'Add a personal recovery email to ensure access to your accomplishments in case you lose access to your #{orgName} account.',
                { orgName: thirdPartyOrganization?.name }
              )
            : _t(
                'Add a personal recovery email to ensure access to your accomplishments in case you lose access to your organization account.'
              )}
        </PageNotification>
      )}
      {showUnverifiedEmailNotification && unverifiedEmail && (
        <PageNotification
          title={_t('Verify your recovery email')}
          severity="warning"
          className="program-sso-notification"
          inlineAction={
            <Button size="small" variant="ghost" onClick={openRecoveryModal}>
              {_t('Resend email')}
            </Button>
          }
        >
          {thirdPartyOrganization
            ? _t(
                'Check your inbox for a verification email sent to #{email}. Verify now to avoid losing access to your #{orgName} account in the future.',
                {
                  email: unverifiedEmail.emailAddress,
                  orgName: thirdPartyOrganization.name,
                }
              )
            : _t(
                'Check your inbox for a verification email sent to #{email}. Verify now to avoid losing access to your organization account in the future.',
                { email: unverifiedEmail.emailAddress }
              )}
        </PageNotification>
      )}
    </>
  );
};

export default RecoveryEmailNotification;
